// Repeater field - renders an array of items with nested fields
// Single-field mode: inline input + add button, pills with X above
// Multi-field mode: card-based layout with nested fields
import { useCallback, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FieldWrapper, Input } from '@startus/ui';
import { useFormMode } from '../../hooks/useFormMode';
import { getFieldComponent } from '../../config/fieldRegistry';
import type { BaseFieldProps, RepeaterFieldConfig, FieldType } from '../../types/fields';

// Pill component for single-field mode
function Pill({
  label,
  onRemove,
  removable = true,
}: {
  label: string;
  onRemove?: () => void;
  removable?: boolean;
}) {
  const { t } = useTranslation('common');
  return (
    <span
      className="badge badge-primary gap-2 py-3 rounded-full flex max-w-[220px] items-center"
      title={label}
    >
      <span className="truncate">{label}</span>
      {removable && onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="hover:opacity-70 shrink-0"
          aria-label={`${t('common.remove')} ${label}`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3 w-3"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </span>
  );
}

export function RepeaterField({
  name,
  placeholder,
  itemFields = [],
  addButtonLabel,
  itemLabelPrefix,
}: BaseFieldProps) {
  const { t } = useTranslation('common');
  // Translated defaults; callers can still override (or pass '' to hide the item prefix).
  const addLabel = addButtonLabel ?? t('formFields.addAnother');
  const itemPrefix = itemLabelPrefix ?? t('formFields.item');
  const { isReadOnly } = useFormMode();
  const { control } = useFormContext();
  const [inputValue, setInputValue] = useState('');

  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  // Detect single-field mode (only one field in itemFields)
  const isSingleFieldMode = itemFields.length === 1;
  const singleFieldConfig = isSingleFieldMode ? itemFields[0] : null;

  // Build default empty item from itemFields config
  const getEmptyItem = useCallback(() => {
    const empty: Record<string, string> = {};
    itemFields.forEach((field) => {
      empty[field.name] = '';
    });
    return empty;
  }, [itemFields]);

  // No auto-seeded blank row on mount: react-hook-form's useFieldArray tracks each array item's
  // dirty state against its own internal baseline snapshot, taken once when the field array is
  // created. An append() called from a mount effect gets recorded as a genuine diff against that
  // baseline no matter what — resetField on the nested leaf paths doesn't reach that snapshot, so
  // the field (and therefore its section, and the global Save button) reads as dirty before the
  // user has touched anything. The empty state below already has an explicit Add button, so the
  // user creates the first row themselves and it's genuinely dirty when they do.

  // Handle add in single-field mode
  const handleAddSingleField = () => {
    if (!inputValue.trim() || !singleFieldConfig) return;
    append({ [singleFieldConfig.name]: inputValue.trim() });
    setInputValue('');
  };

  // Handle key press for single-field mode (Enter to add)
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSingleField();
    }
  };

  // Render a single nested field within an item (multi-field mode)
  const renderNestedField = (itemField: RepeaterFieldConfig, itemIndex: number) => {
    const fieldName = `${name}.${itemIndex}.${itemField.name}`;
    const Component = getFieldComponent(itemField.type as FieldType);

    return (
      <Component
        key={fieldName}
        name={fieldName}
        label={itemField.label}
        placeholder={itemField.placeholder}
        required={itemField.required}
        options={itemField.options}
        helpText={itemField.helpText}
      />
    );
  };

  // Single-field mode: inline input with pills
  if (isSingleFieldMode && singleFieldConfig) {
    const fieldKey = singleFieldConfig.name;

    // Read-only: show pills without remove buttons
    if (isReadOnly) {
      return (
        <FieldWrapper>
          {/*<Label required={required} fieldNumber={fieldNumber}>{label}</Label>*/}
          <div className="flex flex-wrap gap-2 py-2">
            {fields.length > 0 ? (
              fields.map((field) => (
                <Pill
                  key={field.id}
                  label={(field as Record<string, string>)[fieldKey] || ''}
                  removable={false}
                />
              ))
            ) : (
              <span className="text-base-content/60">—</span>
            )}
          </div>
        </FieldWrapper>
      );
    }

    // Editable: input + add button, pills below
    return (
      <FieldWrapper>
        {/*<Label required={required} fieldNumber={fieldNumber}>{label}</Label>*/}
        <div className="space-y-3">
          {/* Input + Add button inline */}
          <div className="flex gap-2 items-center">
            <Input
              type={singleFieldConfig.type === 'url' ? 'url' : 'text'}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={placeholder || singleFieldConfig.placeholder || ''}
              className="flex-1 rounded-lg"
            />
            <button
              type="button"
              onClick={handleAddSingleField}
              disabled={!inputValue.trim()}
              className="btn btn-neutral btn-sm rounded-lg text-xl px-4"
              aria-label={t('common.add')}
            >
              +
            </button>
          </div>

          {/* Added items as pills */}
          {fields.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {fields.map((field, index) => (
                <Pill
                  key={field.id}
                  label={(field as Record<string, string>)[fieldKey] || ''}
                  onRemove={() => remove(index)}
                />
              ))}
            </div>
          )}
        </div>
      </FieldWrapper>
    );
  }

  // Multi-field mode: card-based layout
  return (
    <FieldWrapper>
      {/*<Label required={required} fieldNumber={fieldNumber}>{label}</Label>*/}
      <div className="space-y-6 pb-4">
        {fields.map((field, index) => (
          <div key={field.id} className="space-y-4">
            {/* Item header */}
            <div className="flex items-center justify-between">
              {itemPrefix !== '' && (
                <span className="font-medium text-base-content">
                  {itemPrefix} {index + 1}
                </span>
              )}
              {/* Remove button - only in edit mode and if more than 1 item */}
              {!isReadOnly && fields.length > 1 && (
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="btn btn-ghost btn-sm text-error"
                >
                  {t('common.remove')}
                </button>
              )}
            </div>

            {/* Nested fields */}
            <div className="space-y-4">
              {itemFields.map((itemField) => renderNestedField(itemField, index))}
            </div>
          </div>
        ))}

        {/* Add button - only in edit mode */}
        {!isReadOnly && (
          <div className="w-full flex justify-center items-center">
            <button
              type="button"
              onClick={() => append(getEmptyItem())}
              className="btn btn-sm btn-neutral rounded-xl text-lg"
            >
              + {addLabel}
            </button>
          </div>
        )}
      </div>
    </FieldWrapper>
  );
}
